/**
 * 产品目录防重（Frank 2026-10-23；后端 `catalog_guard` / `purchase_products`）。
 *
 * 后端在三处先问再建：
 *  - 建产品 `POST /asset-mgr/products` → 409 `RULE_VIOLATION_PRODUCT_LIKELY_EXISTS`，
 *    details = `{ asked, candidates, suggested_category }`；选候选 = 用它，「是新的」= 带 `confirmNew` 重发；
 *  - 单条建采购 `POST /procurement/purchases` → 409 `RULE_VIOLATION_PURCHASE_PRODUCT_CHOICE`，
 *    details = `{ asked, candidates }`；选候选 = 带 `productId` 重发，「都不是」= 建产品抽屉建好带 `productId` 重发，或按自由文本行重发；
 *  - 批量建采购 → 拿不准的行不建，`failed[]` 里带同一个 errorCode、`asked`、`candidates`。
 *
 * 选了候选之后都要调 `POST /asset-mgr/products/{id}/aliases` 记住用户的叫法（`rememberProductAlias`）。
 *
 * details 是后端原样的 dict（snake_case 键：`suggested_category`），不经 camelCase 转换。
 */
import { toast } from 'sonner';
import i18n from '@/i18n';
import { productsApi } from '@/api';
import { extractApiErrorCode, isClientRejection } from '@/utils/apiError';
import { asSnowflakeId, type SnowflakeId } from '@/types/snowflake';

/**
 * 待审核的产品挂的系统标签（后端 `pending_products.PENDING_TAG` / `DUPLICATE_TAG`，按名字建、各租户一个）。
 * 按名字筛：标签 id 每个租户不同，名字是后端定死的。
 */
export const PENDING_REVIEW_TAG = 'pending review';
export const POSSIBLE_DUPLICATE_TAG = 'possible duplicate';

export const PRODUCT_LIKELY_EXISTS = 'RULE_VIOLATION_PRODUCT_LIKELY_EXISTS';
export const PURCHASE_PRODUCT_CHOICE = 'RULE_VIOLATION_PURCHASE_PRODUCT_CHOICE';

export interface CatalogCandidate {
  id: SnowflakeId;
  name: string;
  category: string | null;
}

export interface SuggestedCategory {
  id: SnowflakeId;
  name: string;
  path: string;
}

/** 服务端拿不准、要用户选的一次询问。 */
export interface ProductChoice {
  /** 被配的说法（产品名 / 采购描述）。 */
  asked: string;
  candidates: CatalogCandidate[];
  /** 只有建产品的 409 给；是新产品时放进这个分类。 */
  suggestedCategory: SuggestedCategory | null;
}

function toCandidate(raw: unknown): CatalogCandidate | null {
  const c = raw as { id?: unknown; name?: unknown; category?: unknown } | null;
  if (!c || c.id == null || typeof c.name !== 'string') return null;
  return {
    id: asSnowflakeId(String(c.id)),
    name: c.name,
    category: typeof c.category === 'string' && c.category ? c.category : null,
  };
}

function toCategory(raw: unknown): SuggestedCategory | null {
  const c = raw as { id?: unknown; name?: unknown; path?: unknown } | null;
  if (!c || c.id == null || typeof c.name !== 'string') return null;
  return { id: asSnowflakeId(String(c.id)), name: c.name, path: typeof c.path === 'string' ? c.path : c.name };
}

/** 后端的 `{ asked, candidates, suggested_category }`（或批量 failed[] 那一行）→ `ProductChoice`；没有候选回 null。 */
export function toProductChoice(raw: unknown, fallbackAsked = ''): ProductChoice | null {
  const d = raw as { asked?: unknown; candidates?: unknown; suggested_category?: unknown; suggestedCategory?: unknown } | null;
  if (!d || typeof d !== 'object') return null;
  const candidates = (Array.isArray(d.candidates) ? d.candidates : [])
    .map(toCandidate)
    .filter((c): c is CatalogCandidate => c !== null);
  if (candidates.length === 0) return null;
  return {
    asked: typeof d.asked === 'string' && d.asked ? d.asked : fallbackAsked,
    candidates,
    suggestedCategory: toCategory(d.suggested_category ?? d.suggestedCategory),
  };
}

/**
 * 这个错误是不是「目录里可能已有，请用户选」，是就取出候选。
 *
 * 拦截器把 409 翻译成没有 `.response` 的 Error，details 在 `err.details`；未翻译的在 `err.response.data.details`。
 */
export function readProductChoice(err: unknown, codes: string[] = [PRODUCT_LIKELY_EXISTS, PURCHASE_PRODUCT_CHOICE]): ProductChoice | null {
  const code = extractApiErrorCode(err);
  if (!code || !codes.includes(code)) return null;
  const e = err as { details?: unknown; response?: { data?: { details?: unknown } } } | null;
  return toProductChoice(e?.details ?? e?.response?.data?.details);
}

export interface RememberAliasResult {
  saved?: boolean;
  suggested?: boolean;
  skipped?: string | null;
}

/**
 * 用户在候选里选了 `productId`：记住他说的 `text`。不挡流程——失败只记日志；
 * 存成别名 / 记成建议时给一条轻提示，跳过（已是别名、同名等）什么都不说。
 */
export async function rememberProductAlias(productId: SnowflakeId, text: string, productName?: string): Promise<void> {
  const words = text.trim();
  if (!words || (productName && words.toLowerCase() === productName.trim().toLowerCase())) return;
  try {
    const res = (await productsApi.rememberAlias(String(productId), words)) as unknown as RememberAliasResult;
    const values = { text: words, name: productName ?? '' };
    if (res?.saved) toast.success(i18n.t('assets:catalogGuard.aliasSaved', values));
    else if (res?.suggested) toast.message(i18n.t('assets:catalogGuard.aliasSuggested', values));
  } catch (e) {
    console.warn('remember product alias failed', e);
  }
}

export type ProductSuggestStatus = 'matched' | 'ask' | 'new';

export interface ProductSuggestResult {
  status: ProductSuggestStatus;
  product: CatalogCandidate | null;
  candidates: CatalogCandidate[];
  suggestedCategory: SuggestedCategory | null;
  unavailable: boolean;
}

/**
 * 建议要调模型（几秒、要钱）：至少这么多个非空白字符才问；短词搜不到很正常，不值得问。
 */
export const SUGGEST_MIN_CHARS = 3;

/** 这段文字够不够格去问目录（非空白字符数 ≥ SUGGEST_MIN_CHARS）。 */
export function canSuggest(text: string): boolean {
  return text.replace(/\s/g, '').length >= SUGGEST_MIN_CHARS;
}

/** 按文字缓存（不分大小写、空白归一）；同一个词在同一页里只问一次。失败不缓存。 */
const suggestCache = new Map<string, ProductSuggestResult>();
const SUGGEST_CACHE_MAX = 100;
const cacheKey = (text: string) => text.trim().replace(/\s+/g, ' ').toLowerCase();

/** 测试用：清掉缓存。 */
export function clearSuggestCache() {
  suggestCache.clear();
}

/**
 * `POST /asset-mgr/products/suggest`。不够格（见 `canSuggest`）、失败（含 403）、被取消都回 null：
 * 建议只是锦上添花，不挡选择器。`signal` 用来取消过时的请求。
 */
export async function suggestProducts(text: string, signal?: AbortSignal): Promise<ProductSuggestResult | null> {
  const words = text.trim();
  if (!canSuggest(words)) return null;
  const key = cacheKey(words);
  const cached = suggestCache.get(key);
  if (cached) return cached;
  try {
    const raw = (await productsApi.suggest(words, signal)) as unknown as {
      status?: string; product?: unknown; candidates?: unknown[]; suggestedCategory?: unknown; unavailable?: boolean;
    };
    const product = toCandidate(raw?.product);
    const candidates = (raw?.candidates ?? []).map(toCandidate).filter((c): c is CatalogCandidate => c !== null);
    // matched 时 product 排第一，候选里不重复列它
    const list = product ? [product, ...candidates.filter((c) => c.id !== product.id)] : candidates;
    const status = raw?.status === 'matched' || raw?.status === 'ask' ? raw.status : 'new';
    const result: ProductSuggestResult = {
      status,
      product,
      candidates: list,
      suggestedCategory: toCategory(raw?.suggestedCategory),
      unavailable: !!raw?.unavailable,
    };
    // 判断不可用时的结果只是文字搜索，不缓存，下次再问
    if (!result.unavailable) {
      if (suggestCache.size >= SUGGEST_CACHE_MAX) suggestCache.delete(suggestCache.keys().next().value as string);
      suggestCache.set(key, result);
    }
    return result;
  } catch (e) {
    if (!signal?.aborted && !isClientRejection(e)) console.warn('product suggest failed', e);
    return null;
  }
}
